import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../styles/ContractDetail.css';
import { client } from '../middleware/ShopifyClient.jsx';
import { CartContext } from '../middleware/TrackCart.jsx';
import updateDocumentTitle from '../utils/updateDocumentTitle.js';

const ContractDetail = () => {
  const { handle } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addItemToCart, removeItemFromCart, isItemInCart } = useContext(CartContext);

  useEffect(() => {
    const fetchContractDetails = async () => {
      setLoading(true);
      
      const query = `
        {
          product(handle: "${handle}") {
            id
            title
            handle
            description
            descriptionHtml
            variants(first: 1) {
              edges {
                node {
                  id
                }
              }
            }
            images(first: 2) {
              edges {
                node {
                  url
                }
              }
            }
          }
        }
      `;

      try {
        const response = await client.request(query);
        if (!response.data.product) {
          throw new Error('Product not found');
        }
        setContract(response.data.product);
        return updateDocumentTitle(response.data.product.title);
      } catch (err) {
        console.error('Failed to fetch contract details:', err);
        setError(`Failed to load contract details: ${err.message}. Please try again later.`);
      } finally {
        setLoading(false);
      }

      return updateDocumentTitle('Product Page');
    };

    if (handle) {
      fetchContractDetails();
      window.scrollTo(0, 0);
    }
  }, [handle]);

  const handleAddToCart = () => {
    if (contract) {
      addItemToCart(contract);
    }
  };

  const handleRemoveFromCart = () => {
    if (contract) {
      removeItemFromCart(contract.id);
    }
  };

  const [selectedImage, setSelectedImage] = useState(0);

  const selectImage = (index) => {
    setSelectedImage(index);
  };

  if (loading) {
    return (
      <div className="contract-detail-page loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading contract details...</p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="contract-detail-page error">
        <div className="error-container">
          <h2>Oops! Something went wrong</h2>
          <p>{error || 'Contract not found'}</p>
          <Link to="/library" className="back-btn">
            Back to Contract Library
          </Link>
        </div>
      </div>
    );
  }

  const images = contract.images?.edges || [];
  const mainImage = images.length > 0 ? images[selectedImage].node.url : '/images/contract.png';
  const isInCart = isItemInCart(contract.id);

  return (
    <div className="contract-detail-page">
      <div className="contract-detail-header">
        <div className="container">
          <h1>{contract.title}</h1>
          <Link to="/library" className="back-link">
            ← Back to Contract Library
          </Link>
        </div>
      </div>

      <div className="contract-detail-content">
        <div className="contract-info-container">
          <div className="contract-preview">
            <div className="main-image">
              <img src={mainImage} alt={contract.title} className="refined-image" />
            </div>
            {images.length > 1 && (
              <div className="contract-thumbnail-list">
                {images.map((image, index) => (
                  <div 
                    className={`contract-thumbnail ${selectedImage === index ? 'active' : ''}`} 
                    key={index} 
                    onClick={() => selectImage(index)}
                  >
                    <img src={image.node.url} alt={`${contract.title} preview ${index + 1}`} className="refined-thumbnail" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="contract-info">
            <div className="contract-title-section">
              <h2>{contract.title.split('$')[0] || contract.title || ''}</h2>
            </div>

            <div className="contract-description">
              {contract.descriptionHtml ? (
                <div dangerouslySetInnerHTML={{ __html: contract.descriptionHtml }} />
              ) : (
                <>
                  <h3>About this contract</h3>
                  <p><strong>Privacy isn't just polite, it's the law.</strong> This professionally crafted legal template is designed to protect your business interests and provide you with the legal safeguards you need.</p>
                  
                  <h3>Key features</h3>
                  <ul className="fancy-list">
                    <li>Customizable fields for your specific needs</li>
                    <li>Clear and comprehensive legal language</li>
                    <li>Vetted by legal professionals</li>
                    <li>Compatible with common word processors</li>
                    <li>Instant download after purchase</li>
                  </ul>
                  
                  <h3>When to use</h3>
                  <p>This template is ideal for freelancers, small businesses, and entrepreneurs who need professional legal protection without the excessive legal fees.</p>
                </>
              )}
            </div>

            <div className="purchase-section">
              <div className="benefits">
                <div className="benefit">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Instant download</div>
                </div>
                <div className="benefit">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Easy to customize</div>
                </div>
                <div className="benefit">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Professionally drafted</div>
                </div>
              </div>

              {isInCart ? (
                <div className="cart-actions">
                  <button className="remove-btn" onClick={handleRemoveFromCart}>
                    Remove from Cart
                  </button>
                  <Link to="/cart" className="view-cart-btn">
                    View Cart
                  </Link>
                </div>
              ) : (
                <button className="add-to-cart-btn large" onClick={handleAddToCart}>
                  Add to Cart
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="legal-disclaimer-section">
          <div className="important-note">
            <h4>Important Note</h4>
            <p>ContractWell is not a law firm and does not offer legal services or legal advice. The resources provided on this website — including templates, guides and cheat sheet are intended for general informational purposes only and should not be considered a substitute for professional legal counsel.</p>
            <p>Purchasing, downloading, or using any material from ContractWell does not establish an attorney-client or solicitor-client relationship between you and ContractWell or any of its contributors. As each business and legal context is different, we recommend consulting a qualified lawyer in your jurisdiction if you require personalized legal advice for specific issues.</p>
            <p>Please refer to our complete Disclaimer for more information.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractDetail; 